import { getSession, setSession } from '@/auth/session'

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, message: string, body?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api'

type Query = Record<string, string | number | boolean | null | undefined>

function withQuery(path: string, query?: Query): string {
  if (!query) return `${BASE_URL}${path}`
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value))
  }
  const qs = params.toString()
  return `${BASE_URL}${path}${qs ? `?${qs}` : ''}`
}

async function readError(response: Response): Promise<ApiError> {
  let body: unknown
  let message = `${response.status} ${response.statusText}`
  try {
    body = await response.json()
    if (body && typeof body === 'object') {
      const b = body as Record<string, unknown>
      message = String(b.detail ?? b.message ?? b.title ?? b.error ?? message)
    }
  } catch {
    /* no JSON body */
  }
  return new ApiError(response.status, message, body)
}

async function send<T>(method: string, path: string, options: { query?: Query; body?: unknown } = {}): Promise<T> {
  const session = getSession()
  const headers: Record<string, string> = {}
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  if (session) headers.Authorization = `Bearer ${session.credential}`

  const response = await fetch(withQuery(path, options.query), {
    method,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })
  // The token is no longer accepted: dropping it brings back the sign-in card. A 403 keeps it,
  // since the account is fine and only lacks access.
  if (response.status === 401 && session) setSession(null)
  if (!response.ok) throw await readError(response)
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}

export const api = {
  get: <T>(path: string, query?: Query) => send<T>('GET', path, { query }),
  post: <T>(path: string, body?: unknown, query?: Query) => send<T>('POST', path, { body, query }),
  put: <T>(path: string, body?: unknown, query?: Query) => send<T>('PUT', path, { body, query }),
  delete: <T>(path: string, query?: Query) => send<T>('DELETE', path, { query }),
}
